import React, { useEffect, useState } from 'react';
import Loading from '../../../../Components/Loading';
import { useLaboratoryRequestFormQuery } from '../../../../graphql/graphql';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { LaboratoryRequestItem } from '../../Interfaces/LaboratoryRequestItem.intefrace';
import LaboratoryRequestCreateFormItem from './LaboratoryRequestCreateFormItem';
// @ts-ignore
import styles from './LaboratoryRequestCreateForm.module.scss';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import LaboratoryRequestCreateFormSamples from './LaboratoryRequestCreateFormSamples';
import { LaboratoryRequestSample } from '../../Interfaces/LaboratoryRequestSample.interface';
import useFilters from '../../../Base/Hooks/useFilters';
import Text from '../../../../Components/Base/Text/Text';
import useCustomInputs from '../../../Base/Hooks/useCustomInputs';
import testData from './data.json';
import useLaboratoryRequestFormResolver, {
	ResolveHelpItem,
} from '../../Hooks/useLaboratoryRequestFormResolver';
import { isEqual } from 'lodash';

interface Props {
	formId: string;
	onSuccess: (values: LaboratoryRequestFormDataScheme[]) => void;
}

export interface LaboratoryRequestCreateFormScheme {
	children: LaboratoryRequestItem[];
	samples?: LaboratoryRequestSample[];
	settings?: {};
}

export interface LaboratoryRequestFormDataScheme {
	key: string;
	wizardItemId: string;
	value: string;
	note?: string;
}

const LaboratoryRequestCreateForm: React.FC<Props> = ({
	formId,
	onSuccess,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [formScheme, setFormScheme] = useState<
		LaboratoryRequestCreateFormScheme | undefined
	>(undefined);
	const [activeSamples, setActiveSamples] = useState<string[]>([]);
	const [activeItems, setActiveItems] = useState<LaboratoryRequestItem[]>([]);

	const { formResolveHelperData } =
		useLaboratoryRequestFormResolver(formScheme);

	const { checkEmptyString } = useFilters();
	const { handleSplitGroupCheckbox, checkIsGroupCheckbox } = useCustomInputs();

	const { data, isLoading: dataIsLoading } = useLaboratoryRequestFormQuery({
		where: { id: formId },
	});

	const handleItemSelect = (item: LaboratoryRequestItem) => {
		setActiveItems(prev => {
			if (prev.some(i => i.value === item.value)) {
				return [...prev.filter(i => i.value !== item.value)];
			} else {
				return [...prev, item];
			}
		});
	};

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		const result: LaboratoryRequestFormDataScheme[] = [];

		const pushToResult = ({
			helperItem,
			key,
			value,
		}: {
			helperItem: ResolveHelpItem;
			key: string;
			value: string;
		}) => {
			result.push({
				wizardItemId: helperItem.wizardItemId,
				note: helperItem.name,
				key,
				value,
			});
		};

		const findHelperItemByKey = (key: any): ResolveHelpItem | undefined => {
			return formResolveHelperData.find(h => isEqual(h.key, key));
		};

		for (const resultKey in values) {
			const value = values[resultKey];
			if (typeof value === 'boolean' && value) {
				//RESOLVING BOOLEAN VALUES, NEED TO FILTER ONLY TRUE VALUES AND ADD THEM INTRO FINAL RESULT AS STRING
				const helperItem = checkIsGroupCheckbox(resultKey)
					? findHelperItemByKey(handleSplitGroupCheckbox(resultKey, value))
					: findHelperItemByKey(resultKey);
				if (helperItem) {
					pushToResult({
						helperItem,
						key: resultKey,
						value: 'true',
					});
				}
			} else if (typeof value === 'string' && checkEmptyString(value)) {
				// RESOLVING TYPE STRING, JUST REMOVING EMPTY FIELDS AND PUSHING REAL ENTERED VALUE
				const helperItem = findHelperItemByKey(resultKey);
				if (helperItem) {
					pushToResult({
						helperItem,
						key: resultKey,
						value,
					});
				}
			}
		}

		setIsLoading(false);
		onSuccess(result);
	};

	const formMethods = useForm();

	useEffect(() => {
		setIsLoading(true);
		//setFormScheme(testData as LaboratoryRequestCreateFormScheme);

		if (data?.laboratoryRequisitionForm?.form) {
			setFormScheme(
				data.laboratoryRequisitionForm
					.form as LaboratoryRequestCreateFormScheme,
			);
		}
		setIsLoading(false);
	}, [data]);

	useEffect(() => {
		const tempSamples: string[] = [];
		activeItems.forEach(item => {
			item.samples?.forEach(sample => {
				if (!tempSamples.includes(sample)) {
					tempSamples.push(sample);
				}
			});
		});
		setActiveSamples(tempSamples);
	}, [activeItems]);

	if (isLoading || dataIsLoading) {
		return <Loading />;
	}

	if (!formScheme?.children?.length) {
		return <Text tx='component.laboratoryRequestCreateForm.schemeNotFound' />;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.formWrapper}>
				<FormWrapper formMethods={formMethods}>
					{formScheme?.children.map((item, key) => (
						<LaboratoryRequestCreateFormItem
							item={item}
							key={key}
							onItemSelect={handleItemSelect}
						/>
					))}
					<div className='mt-4'>
						<SubmitButton
							onSubmit={handleSubmit}
							title='base.shared.continue'
						/>
					</div>
				</FormWrapper>
			</div>
			{(formScheme?.samples?.length || 0) > 0 && (
				<LaboratoryRequestCreateFormSamples
					activeSamples={activeSamples}
					items={formScheme.samples || []}
				/>
			)}
		</div>
	);
};

export default LaboratoryRequestCreateForm;
